/**
 * PromptDialog — pretty replacement for `window.prompt` with single-line
 * or multi-line input support.
 *
 * Usage:
 *   const [open, setOpen] = useState(false)
 *   <button onClick={() => setOpen(true)}>新增</button>
 *   <PromptDialog open={open} onOpenChange={setOpen}
 *     title="新增文章" label="標題" multiline rows={6}
 *     onSubmit={(value) => createArticle(value)} />
 *
 * Note: callers that want a clean reset across opens should pass a `key`
 * that changes between open/close cycles (we don't read the prop; the parent
 * decides) OR rely on `defaultValue` matching per open. We intentionally
 * do NOT mutate state on `open` via useEffect because that races with the
 * AnimatePresence exit animation.
 */
import { useState } from 'react'
import {
  DialogRoot,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from './Dialog'

interface PromptDialogProps {
  open: boolean
  onOpenChange: (v: boolean) => void
  title: string
  message?: string
  label?: string
  defaultValue?: string
  multiline?: boolean
  rows?: number
  maxLength?: number
  submitLabel?: string
  cancelLabel?: string
  /** Returns error message or null. */
  validate?: (value: string) => string | null
  onSubmit: (value: string) => void | Promise<void>
}

export function PromptDialog({
  open,
  onOpenChange,
  title,
  message,
  label,
  defaultValue = '',
  multiline = false,
  rows = 4,
  maxLength,
  submitLabel = '確認',
  cancelLabel = '取消',
  validate,
  onSubmit,
}: PromptDialogProps) {
  const [value, setValue] = useState(defaultValue)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <DialogRoot open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v) }}>
      <DialogHeader>
        <DialogTitle className="text-lg font-bold text-ink">{title}</DialogTitle>
        {message && <DialogDescription className="text-sm text-ink-soft">{message}</DialogDescription>}
      </DialogHeader>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          if (busy) return
          const err = validate?.(value)
          if (err) {
            setError(err)
            return
          }
          setBusy(true)
          try {
            await onSubmit(value)
            onOpenChange(false)
          } catch (err) {
            setError(err instanceof Error ? err.message : '操作失敗')
          } finally {
            setBusy(false)
          }
        }}
      >
        {label && (
          <label htmlFor="prompt-dialog-input" className="block text-sm font-semibold text-ink mb-1">
            {label}
          </label>
        )}
        {multiline ? (
          <textarea
            id="prompt-dialog-input"
            value={value}
            onChange={(e) => { setValue(e.target.value); setError(null) }}
            rows={rows}
            maxLength={maxLength}
            autoFocus
            disabled={busy}
            className="w-full rounded-yada-md border border-surface-3 bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
          />
        ) : (
          <input
            id="prompt-dialog-input"
            type="text"
            value={value}
            onChange={(e) => { setValue(e.target.value); setError(null) }}
            maxLength={maxLength}
            autoFocus
            disabled={busy}
            className="w-full rounded-yada-md border border-surface-3 bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
          />
        )}
        {maxLength !== undefined && (
          <p className="mt-1 text-xs text-ink-muted text-right">
            {value.length} / {maxLength}
          </p>
        )}
        {error && (
          <p role="alert" className="mt-2 text-sm text-danger">
            {error}
          </p>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <button
              type="button"
              disabled={busy}
              className="px-4 py-1.5 rounded-yada-full text-sm font-semibold border-2 border-surface-3 text-ink hover:bg-surface-2 disabled:opacity-50"
            >
              {cancelLabel}
            </button>
          </DialogClose>
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-1.5 rounded-yada-full text-sm font-semibold bg-primary text-surface hover:bg-primary-dark disabled:opacity-50"
          >
            {busy ? '處理中…' : submitLabel}
          </button>
        </DialogFooter>
      </form>
    </DialogRoot>
  )
}